import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '../store/useAppStore';
import { Volume2, VolumeX } from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';

interface GeneratingVideoModalProps {
  isOpen: boolean;
  onComplete: () => void;
  isGenerating?: boolean;
  mediaType?: 'image' | 'video' | 'audio';
}

export const GeneratingVideoModal: React.FC<GeneratingVideoModalProps> = ({
  isOpen,
  onComplete,
  isGenerating = true,
  mediaType = 'video',
}) => {
  const { session } = useAppStore();
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoStartTime, setVideoStartTime] = useState<number | null>(null);
  const [canCheckGeneration, setCanCheckGeneration] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isGeneratingRef = useRef(isGenerating);
  isGeneratingRef.current = isGenerating;
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const messageIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const creativeMessages = [
    "Unleashing the algorithms, one pixel at a time...",
    "Our AI is brewing brilliance, please stand by...",
    "Witnessing the birth of digital dreams...",
    "The future is rendering, almost there!",
    "Crafting creativity with code and magic...",
    "Deep learning in progress, expect wonders!",
    "Transforming imagination into reality...",
    "Your masterpiece is being meticulously made...",
  ];

  const getVideoUrl = (): string => {
    const countryCode = session?.hy_country_code?.toLowerCase();
    if (countryCode === 'IN') {
      return 'https://qsiwkqsdtbfftmxbnhie.supabase.co/storage/v1/object/public/public/6eb62ebc-0266-415f-acdc-3bdd525b036f.mp4';
    } else {
      return 'https://qsiwkqsdtbfftmxbnhie.supabase.co/storage/v1/object/public/public/c1c4d04b-aa50-4230-b73c-770dac2fe1d0.mp4';
    }
  };

  const videoUrl = getVideoUrl();

  const getTotalDuration = (): number => {
    switch (mediaType) {
      case 'image': return 60;
      case 'video': return 180;
      case 'audio': return 150;
      default: return 180;
    }
  };

  const totalDuration = getTotalDuration();

  const stopVideoAndComplete = useCallback(() => {
    if (videoRef.current) videoRef.current.pause();
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    if (!isOpen) return;
    setProgress(0);
    if (totalDuration <= 0) return;
    const intervalTime = Math.max(200, (totalDuration * 1000) / 100);
    progressIntervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev < 100) return prev + 1;
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        return 100;
      });
    }, intervalTime);
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [isOpen, totalDuration]);

  useEffect(() => {
    if (isVideoPlaying && !videoStartTime) {
      const startTime = Date.now();
      setVideoStartTime(startTime);

      const timer = setTimeout(() => {
        setCanCheckGeneration(true);
      }, 40000);

      return () => clearTimeout(timer);
    }
  }, [isVideoPlaying, videoStartTime]);

  useEffect(() => {
    if (canCheckGeneration && isGenerating) {
      checkIntervalRef.current = setInterval(() => {
        if (!isGeneratingRef.current) {
          stopVideoAndComplete();
        }
      }, 500);

      return () => {
        if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      };
    }
  }, [canCheckGeneration, isGenerating, stopVideoAndComplete]);

  useEffect(() => {
    if (!isGenerating && videoStartTime && !canCheckGeneration) {
      const elapsed = Date.now() - videoStartTime;
      const remaining = Math.max(0, 40000 - elapsed);

      if (remaining > 0) {
        const timer = setTimeout(stopVideoAndComplete, remaining);
        return () => clearTimeout(timer);
      }
      stopVideoAndComplete();
    }
  }, [isGenerating, videoStartTime, canCheckGeneration, stopVideoAndComplete]);

  useEffect(() => {
    if (isOpen) {
      messageIntervalRef.current = setInterval(() => {
        setCurrentMessageIndex(prev => (prev + 1) % creativeMessages.length);
      }, 5000);
    }
    return () => {
      if (messageIntervalRef.current) clearInterval(messageIntervalRef.current);
    };
  }, [isOpen, creativeMessages.length]);

  useEffect(() => {
    if (isOpen && videoRef.current) {
      const video = videoRef.current;

      const handleLoadedData = () => {
        setIsVideoLoaded(true);
        video.play().then(() => {
          setIsVideoPlaying(true);
        }).catch(() => {
          setIsVideoPlaying(true);
        });
      };

      if (videoRef.current) videoRef.current.muted = isMuted;

      const handlePlay = () => setIsVideoPlaying(true);
      const handlePause = () => setIsVideoPlaying(false);

      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);

      video.load();

      return () => {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
      };
    }
  }, [isOpen, isMuted]);

  useEffect(() => {
    if (!isOpen) {
      setIsVideoPlaying(false);
      setIsVideoLoaded(false);
      setVideoStartTime(null);
      setCanCheckGeneration(false);

      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-base flex items-center justify-center"
      style={{ zIndex: 99999 }}
    >
      <div className="w-full h-full flex flex-col items-center justify-center p-4">
        <div className="relative mb-8">
          <button
            onClick={() => {
              if (videoRef.current) {
                videoRef.current.muted = !isMuted;
                setIsMuted(!isMuted);
              }
            }}
            className="absolute top-4 right-4 z-10 p-2.5 bg-base/60 hover:bg-base/80 text-white rounded-xl transition-all backdrop-blur-sm"
          >
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>

          <video
            ref={videoRef}
            className="rounded-2xl shadow-2xl"
            style={{ height: '400px', width: 'auto', maxWidth: '90vw' }}
            loop
            muted={isMuted}
            playsInline
            preload="auto"
            onError={() => setIsVideoLoaded(false)}
          >
            <source src={videoUrl} type="video/mp4" />
          </video>

          {!isVideoLoaded && (
            <div
              className="absolute inset-0 bg-elevated rounded-2xl flex items-center justify-center"
              style={{ height: '400px' }}
            >
              <div className="text-center space-y-4">
                <LoadingSpinner size="lg" />
                <p className="text-th-secondary">Loading video...</p>
              </div>
            </div>
          )}
        </div>

        <div className="w-full max-w-md bg-elevated rounded-full h-2.5 mb-4">
          <div
            className="bg-brand h-2.5 rounded-full transition-all duration-500 ease-in-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        <p className="text-th-secondary text-sm text-center mb-2">
          {creativeMessages[currentMessageIndex]}
        </p>
        <p className="text-th-tertiary text-xs text-center mb-6 max-w-md mx-auto">
          Video models can take many minutes. If this screen stays too long, check the browser Network tab for{' '}
          <code className="text-th-secondary">fal-generation</code> — or use a background/long-running model for heavy jobs.
        </p>

        <div className="text-center">
          <div className="bg-elevated border border-subtle rounded-2xl px-8 py-6 space-y-4">
            <div className="flex items-center justify-center space-x-3">
              <LoadingSpinner size="sm" />
              <h2 className="text-th-primary font-bold text-xl">
                Please Wait, Hyper AI is Creating Your Request
              </h2>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
